'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter, useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { trackLogin } from '@/lib/analytics';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface ForgotFormData {
  resetEmail: string;
}

const DEMO_CREDENTIALS = { email: 'demo@contactsense.ai', password: 'Demo@ContactSense2026' };

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { signIn, resetPasswordForEmail } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>();

  const {
    register: registerReset,
    handleSubmit: handleResetSubmit,
    formState: { errors: resetErrors },
  } = useForm<ForgotFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      trackLogin('email');
      toast.success('Signed in successfully');
      // Honour the redirect param — fall back to dashboard
      const redirectTo = searchParams?.get('redirect');
      const destination = redirectTo && redirectTo.startsWith('/') ? redirectTo : '/';
      router.push(destination);
      router.refresh();
    } catch (err: any) {
      toast.error(err?.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onResetSubmit = async (data: ForgotFormData) => {
    setResetLoading(true);
    try {
      await resetPasswordForEmail(data.resetEmail);
      setResetSent(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  if (showForgot) {
    return (
      <div className="flex flex-col gap-6">
        <div>
          <button
            type="button"
            onClick={() => { setShowForgot(false); setResetSent(false); }}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <Icon name="ArrowLeftIcon" size={14} />
            Back to sign in
          </button>
          <h2 className="text-2xl font-800 text-foreground">Reset your password</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Enter your email and we&apos;ll send you a link to reset your password.
          </p>
        </div>

        {resetSent ? (
          <div className="rounded-xl border border-border bg-muted/60 p-5 flex flex-col items-center gap-3 text-center">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="EnvelopeIcon" size={20} className="text-primary" />
            </div>
            <p className="text-sm font-600 text-foreground">Check your inbox</p>
            <p className="text-xs text-muted-foreground">
              We&apos;ve sent a password reset link to your email address. It may take a minute to arrive.
            </p>
            <button
              type="button"
              onClick={() => { setShowForgot(false); setResetSent(false); }}
              className="btn-primary text-sm py-2 px-4 mt-1"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleResetSubmit(onResetSubmit)} className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-600 text-foreground mb-1.5">Email address</label>
              <input
                type="email"
                autoComplete="email"
                className={`input-base ${resetErrors.resetEmail ? 'input-error' : ''}`}
                placeholder="you@company.com"
                {...registerReset('resetEmail', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                })}
              />
              {resetErrors.resetEmail && (
                <p className="text-xs text-danger mt-1">{resetErrors.resetEmail.message}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={resetLoading}
              className="btn-primary w-full justify-center text-sm py-2.5"
            >
              {resetLoading ? (
                <>
                  <Icon name="ArrowPathIcon" size={15} className="animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-800 text-foreground">Welcome back</h2>
        <p className="text-sm text-muted-foreground mt-1">Sign in to your ContactSense workspace</p>
      </div>

      {/* Social auth */}
      <div className="flex flex-col gap-2">
        <button className="btn-secondary w-full justify-center text-sm">
          <Icon name="GlobeAltIcon" size={16} />
          Continue with Google
        </button>
        <button className="btn-secondary w-full justify-center text-sm">
          <Icon name="CodeBracketIcon" size={16} />
          Continue with GitHub
        </button>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">or sign in with email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Email */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Email address</label>
          <input
            type="email"
            autoComplete="email"
            className={`input-base ${errors.email ? 'input-error' : ''}`}
            placeholder="you@company.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-600 text-foreground">Password</label>
            <button
              type="button"
              onClick={() => setShowForgot(true)}
              className="text-xs text-primary hover:underline font-500"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              className={`input-base pr-10 ${errors.password ? 'input-error' : ''}`}
              placeholder="Your password"
              {...register('password', { required: 'Password is required' })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.password && <p className="text-xs text-danger mt-1">{errors.password.message}</p>}
        </div>

        {/* Remember me */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" {...register('rememberMe')} className="w-3.5 h-3.5 rounded accent-primary" />
          <span className="text-sm text-muted-foreground">Remember me for 30 days</span>
        </label>

        <button type="submit" disabled={loading} className="btn-primary w-full justify-center text-sm py-2.5">
          {loading ? (
            <>
              <Icon name="ArrowPathIcon" size={15} className="animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      {/* Demo credentials box */}
      <div className="rounded-xl border border-border bg-muted/60 p-4">
        <p className="text-[11px] font-700 uppercase tracking-widest text-muted-foreground mb-3">Demo Credentials</p>
        <div className="flex flex-col gap-2">
          {[
            { label: 'Email', value: DEMO_CREDENTIALS.email },
            { label: 'Password', value: DEMO_CREDENTIALS.password },
          ].map((cred) => (
            <div key={`cred-${cred.label}`} className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-16">{cred.label}</span>
              <code className="flex-1 text-xs bg-card border border-border rounded px-2 py-1 text-foreground font-mono truncate">{cred.value}</code>
              <button
                type="button"
                onClick={() => {
                  if (cred.label === 'Email') setValue('email', cred.value);
                  else setValue('password', cred.value);
                  toast.success(`${cred.label} copied to form`);
                }}
                className="text-xs text-primary font-600 hover:underline flex-shrink-0"
              >
                Use
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}